/**
 * Sonidos del sistema — sintetizados con Web Audio API.
 *
 * Zero assets: todos los tonos se generan programáticamente con osciladores
 * + envelope ADSR. Sutil, moderno, minimalista. Volumen global bajo (0.08)
 * para no molestar.
 *
 * Respeta dos condiciones:
 *  1. `localStorage.getItem('manaxer.sounds')` === '0' → silencio total
 *  2. `prefers-reduced-motion: reduce` → silencio total (accesibilidad)
 *
 * Uso:
 *   import { playSound } from '@/lib/sounds';
 *   playSound('drawer_open');
 */

type SoundName = 'drawer_open' | 'drawer_close' | 'success' | 'notify' | 'alert';

const STORAGE_KEY = 'manaxer.sounds';
let audioCtx: AudioContext | null = null;

function isEnabled(): boolean {
  if (typeof window === 'undefined') return false;
  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (stored === '0') return false;
  try {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (mq.matches) return false;
  } catch {
    /* noop */
  }
  return true;
}

function ctx(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  if (audioCtx) return audioCtx;
  const Ctor =
    window.AudioContext ||
    (window as unknown as { webkitAudioContext?: typeof AudioContext })
      .webkitAudioContext;
  if (!Ctor) return null;
  audioCtx = new Ctor();
  return audioCtx;
}

interface Tone {
  freq: number;
  /** inicio del tono (segundos desde now) */
  at: number;
  /** duración (segundos) */
  dur: number;
  /** pico de gain (0..1) */
  peak?: number;
  type?: OscillatorType;
  /** slide a otra frecuencia */
  slideTo?: number;
}

function play(tones: Tone[]): void {
  if (!isEnabled()) return;
  const ac = ctx();
  if (!ac) return;
  // Si el contexto está suspendido (autoplay policy), intentar resumir
  if (ac.state === 'suspended') void ac.resume().catch(() => {});

  const now = ac.currentTime;
  for (const t of tones) {
    const osc = ac.createOscillator();
    osc.type = t.type ?? 'sine';
    osc.frequency.setValueAtTime(t.freq, now + t.at);
    if (t.slideTo) {
      osc.frequency.exponentialRampToValueAtTime(t.slideTo, now + t.at + t.dur * 0.95);
    }

    const gain = ac.createGain();
    const peak = t.peak ?? 0.08;
    // ADSR breve
    gain.gain.setValueAtTime(0, now + t.at);
    gain.gain.linearRampToValueAtTime(peak, now + t.at + 0.008);
    gain.gain.linearRampToValueAtTime(peak * 0.7, now + t.at + t.dur * 0.3);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + t.at + t.dur);

    osc.connect(gain).connect(ac.destination);
    osc.start(now + t.at);
    osc.stop(now + t.at + t.dur + 0.02);
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// Biblioteca de sonidos
// ═══════════════════════════════════════════════════════════════════════════
const LIBRARY: Record<SoundName, () => Tone[]> = {
  // Chirp ascendente corto — apertura de panel
  drawer_open: () => [
    { freq: 420, slideTo: 720, at: 0, dur: 0.09, type: 'sine', peak: 0.06 },
  ],
  // Chirp descendente corto — cierre
  drawer_close: () => [
    { freq: 720, slideTo: 380, at: 0, dur: 0.09, type: 'sine', peak: 0.05 },
  ],
  // Doble ding claro — proceso terminado OK (C5 → E5)
  success: () => [
    { freq: 523.25, at: 0, dur: 0.12, type: 'sine', peak: 0.07 },
    { freq: 659.25, at: 0.08, dur: 0.18, type: 'sine', peak: 0.07 },
  ],
  // Ping suave único — notificación
  notify: () => [{ freq: 880, at: 0, dur: 0.22, type: 'sine', peak: 0.06 }],
  // Tres blips cortos descendentes — atención (error/aviso)
  alert: () => [
    { freq: 780, at: 0, dur: 0.06, type: 'triangle', peak: 0.07 },
    { freq: 620, at: 0.08, dur: 0.06, type: 'triangle', peak: 0.07 },
    { freq: 490, at: 0.16, dur: 0.08, type: 'triangle', peak: 0.07 },
  ],
};

export function playSound(name: SoundName): void {
  try {
    const tones = LIBRARY[name]();
    play(tones);
  } catch {
    /* never break UI by sound */
  }
}

export function setSoundsEnabled(enabled: boolean): void {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(STORAGE_KEY, enabled ? '1' : '0');
}

export function areSoundsEnabled(): boolean {
  return isEnabled();
}
